import React from "react";
import { Typography, Divider, Tag } from "antd";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  Database,
  Shield,
  Clock,
  BarChart3,
  Cpu,
  Network,
  Brain,
  Zap,
  Factory,
  Leaf,
  HeartPulse,
  GraduationCap,
  ShoppingBag,
  Truck,
  Home,
  Building2,
} from "lucide-react";

const { Title, Text, Paragraph } = Typography;

const Solutions = () => {
  // 解决方案数据
  const solutions = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI智能芯片解决方案",
      description:
        "为各种机器人和智能设备提供强大的AI智能芯片，实现即插即用的智能化体验。",
      features: ["微型化设计", "自主学习能力", "低功耗运行", "多设备兼容"],
      image: require("../assets/img/cpu_v_1.webp"),
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: "工业自动化解决方案",
      description: "利用AI技术优化工业生产流程，提高生产效率，降低运营成本。",
      features: ["智能控制", "实时监控", "预测维护", "自动化调度"],
      image: require("../assets/img/computer_networks.webp"),
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "农业智能化解决方案",
      description: "通过AI技术实现精准农业，提高农作物产量，减少资源浪费。",
      features: ["智能灌溉", "作物监测", "病虫害预警", "收获预测"],
      image: require("../assets/img/uav.webp"),
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "服务机器人解决方案",
      description:
        "为各类服务机器人提供智能化解决方案，包括家庭服务、酒店服务、医疗辅助等。",
      features: ["自然语言处理", "环境感知", "自主导航", "个性化服务"],
      image: require("../assets/img/robot_cooking_in_the_kitchen.webp"),
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "GAIA开发平台",
      description:
        "提供完整的AI开发平台，帮助开发者快速构建基于AI智能芯片的应用。",
      features: ["可视化开发", "丰富API库", "模拟测试工具", "云端部署支持"],
      image: require("../assets/img/girl_robot_v_1.webp"),
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "动态场景自适应",
      description:
        "为智慧城市、智慧园区、智慧家庭等提供动态场景自适应解决方案。",
      features: [
        "实时数据采集",
        "动态场景分析",
        "智能决策支持",
        "持续优化升级",
      ],
      image: require("../assets/img/computer_networks.webp"),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-300 to-indigo-100 py-16 pt-20 pb-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <Title level={1} className="mb-4">
                解决方案
              </Title>
              <div className="text-gray-600 py-6 text-base">
                基于先进的AI技术，为各行业提供智能解决方案，推动数字化转型和智能化升级
              </div>
              <div className="flex gap-4 mt-2 mb-6">
                <Button variant="blue-griadient" size="lg">
                  了解更多
                </Button>
                <Button variant="secondary" size="lg">
                  联系我们
                </Button>
              </div>
            </div>
            <div className="md:w-1/2 max-w-[560px]">
              <img src={require("../assets/img/solution_hero.webp")} />
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              我们的解决方案
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              为各行业提供智能解决方案
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              基于先进的AI技术，我们为不同行业提供定制化的智能解决方案，帮助客户实现数字化转型和智能化升级
            </p>
          </div>

          {/* Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                    {solution.icon}
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-slate-800">
                    {solution.title}
                  </h3>
                  <p className="mb-4 text-slate-600 text-sm">
                    {solution.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {solution.features.map((feature, idx) => (
                      <Tag key={idx} color="blue" className="text-xs">
                        {feature}
                      </Tag>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" className="w-full">
                    了解详情
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Advantages */}
      <section className="bg-gray-100 py-24">
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              技术优势
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              领先的技术实力
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              我们拥有先进的AI技术和强大的研发团队，为客户提供高质量的智能解决方案
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                先进的芯片技术
              </h3>
              <p className="text-slate-600 text-sm">
                自主研发的AI芯片，具有高性能、低功耗、小体积等特点
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Brain className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                强大的AI算法
              </h3>
              <p className="text-slate-600 text-sm">
                基于深度学习的先进算法，实现自主学习和智能决策
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Network className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                完整的生态系统
              </h3>
              <p className="text-slate-600 text-sm">
                提供从芯片到应用的完整解决方案，支持多种行业应用
              </p>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-slate-800">
                高效的服务支持
              </h3>
              <p className="text-slate-600 text-sm">
                专业的技术团队，提供全方位的服务支持和技术咨询
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Areas */}
      <section
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              应用领域
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              广泛的行业应用
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              我们的解决方案已在多个行业得到应用，取得了显著的成效
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                name: "制造业",
                icon: <Factory className="w-5 h-5" />,
                gradient: "from-blue-500 to-cyan-500",
              },
              {
                name: "农业",
                icon: <Leaf className="w-5 h-5" />,
                gradient: "from-green-500 to-emerald-500",
              },
              {
                name: "医疗健康",
                icon: <HeartPulse className="w-5 h-5" />,
                gradient: "from-red-500 to-pink-500",
              },
              {
                name: "教育",
                icon: <GraduationCap className="w-5 h-5" />,
                gradient: "from-purple-500 to-indigo-500",
              },
              {
                name: "零售",
                icon: <ShoppingBag className="w-5 h-5" />,
                gradient: "from-yellow-500 to-amber-500",
              },
              {
                name: "物流",
                icon: <Truck className="w-5 h-5" />,
                gradient: "from-orange-500 to-red-500",
              },
              {
                name: "智能家居",
                icon: <Home className="w-5 h-5" />,
                gradient: "from-teal-500 to-cyan-500",
              },
              {
                name: "智慧城市",
                icon: <Building2 className="w-5 h-5" />,
                gradient: "from-blue-600 to-indigo-600",
              },
            ].map((industry, index) => (
              <div
                key={index}
                className="group relative p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                <div
                  className="absolute inset-0 bg-gradient-to-br opacity-5 group-hover:opacity-10 transition-opacity duration-300"
                  style={{
                    backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                  }}
                  className={`${industry.gradient}`}
                ></div>
                <div
                  className={`inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-gradient-to-br rounded-lg shadow-lg ${industry.gradient}`}
                >
                  {industry.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2 relative z-10">
                  {industry.name}
                </h3>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent ${industry.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="relative py-24">
        <div
          className="absolute inset-0 bg-blue-600 bg-center bg-cover"
          style={{ backgroundImage: "url('/img/pattern-blue.svg')" }}
        ></div>
        <div className="relative container text-center text-white py-20 z-10">
          <div className="md:max-w-5xl mb-12 mx-auto">
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight">
              成功案例
            </h2>
            <p className="text-base md:text-lg text-blue-100 mb-12">
              我们的解决方案已在多个客户项目中得到应用，取得了显著的成效
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "智能制造",
                description:
                  "帮助某制造企业实现生产线智能化改造，生产效率提升30%",
                client: "某大型制造企业",
              },
              {
                title: "智慧农业",
                description:
                  "为某农场提供智能灌溉和作物监测系统，水资源节约40%",
                client: "某现代化农场",
              },
              {
                title: "服务机器人",
                description: "为酒店行业提供智能服务机器人，提升客户体验",
                client: "某连锁酒店集团",
              },
            ].map((caseStudy, index) => (
              <div
                key={index}
                className="p-8 bg-white/10 backdrop-blur-md rounded-lg"
              >
                <h3 className="mb-4 text-xl font-bold">{caseStudy.title}</h3>
                <p className="text-base text-blue-100 mb-4">
                  {caseStudy.description}
                </p>
                <p className="text-sm text-blue-200">{caseStudy.client}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Button
              variant="secondary"
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              查看更多案例
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl shadow-xl overflow-hidden">
            <div className="flex flex-col md:flex-row items-center p-8 md:p-12 gap-8">
              <div className="md:w-3/5">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  准备好开始您的智能化之旅了吗？
                </h2>
                <p className="text-blue-100 mb-6">
                  联系我们，了解更多关于我们的智能解决方案，让我们一起推动数字化转型和智能化升级
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="secondary"
                    size="lg"
                    className="bg-white text-blue-600 hover:bg-blue-50"
                  >
                    立即咨询
                  </Button>
                </div>
              </div>
              <div className="md:w-2/5">
                <img
                  src={require("../assets/img/girl_robot_v_1.webp")}
                  alt="智能机器人"
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Solutions;
